import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { requestAccess } from "@/lib/memberApi";
import { PageShell, PageHero } from "@/components/layout/PageShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast, Toaster } from "sonner";
import { CheckCircle2 } from "lucide-react";

const RequestAccess = () => {
  const navigate = useNavigate();
  const [districts, setDistricts] = useState([]);
  const [form, setForm] = useState({ name: "", mobile: "", email: "", district: "", registration_no: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.districts().then(setDistricts); }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\d{10}$/.test(form.mobile) || !form.district) { toast.error("Please fill name, a valid 10-digit mobile and district"); return; }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { toast.error("Please enter a valid email address"); return; }
    setBusy(true);
    try { await requestAccess(form); setDone(true); toast.success("Request submitted"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not submit. Try again."); }
    finally { setBusy(false); }
  };

  return (
    <PageShell>
      <Toaster position="top-center" richColors />
      <PageHero title="Request access" subtitle="Not on the member list yet? Submit your details for the admin or your district coordinator to review." />
      <div className="gmoaa-container max-w-xl py-10">
        {done ? (
          <div className="rounded-2xl border border-gmoaa-green/30 bg-gmoaa-green-light p-8 text-center">
            <CheckCircle2 size={36} className="mx-auto text-gmoaa-green" />
            <p className="mt-3 font-serif text-xl font-semibold text-gmoaa-green">Request submitted</p>
            <p className="mt-1 text-sm text-gmoaa-text-muted">You'll be added to the roster after review. You can then log in with Google or an email code.</p>
            <Button onClick={() => navigate("/member/login")} className="mt-5 bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Go to login</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 rounded-2xl border border-gmoaa-border-subtle bg-white p-6">
            <div><Label className="mb-1.5 block text-sm">Full name *</Label><Input value={form.name} onChange={set("name")} className="border-gmoaa-border-subtle" /></div>
            <div><Label className="mb-1.5 block text-sm">Mobile number *</Label><Input value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) }))} className="border-gmoaa-border-subtle" /></div>
            <div><Label className="mb-1.5 block text-sm">Email *</Label><Input type="email" data-testid="request-access-email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="border-gmoaa-border-subtle" /></div>
            <div>
              <Label className="mb-1.5 block text-sm">District *</Label>
              <Select value={form.district} onValueChange={set("district")}>
                <SelectTrigger className="border-gmoaa-border-subtle"><SelectValue placeholder="Select district" /></SelectTrigger>
                <SelectContent>{districts.map((d) => <SelectItem key={d.id} value={d.name_en}>{d.name_en}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block text-sm">Registration number</Label><Input value={form.registration_no} onChange={set("registration_no")} className="border-gmoaa-border-subtle" /></div>
            <div><Label className="mb-1.5 block text-sm">Message (optional)</Label><Textarea rows={3} value={form.message} onChange={set("message")} className="border-gmoaa-border-subtle" /></div>
            <p className="text-xs text-gmoaa-text-muted">Your details are used only for association membership review (DPDP notice — draft, pending legal review).</p>
            <Button type="submit" disabled={busy} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">{busy ? "Submitting..." : "Submit request"}</Button>
          </form>
        )}
      </div>
    </PageShell>
  );
};

export default RequestAccess;
